import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';

const ProductSpecificationsSection = () => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('specifications');

  // Example product data
  const productSpecs = {
    technologies: [
      {
        name: {
          fr: 'Technologie à ultrasons',
          ar: 'تقنية الموجات فوق الصوتية',
          en: 'Ultrasonic Technology'
        },
        description: {
          fr: 'Notre gamme de débitmètres à ultrasons utilise des techniques avancées de mesure du temps de transit pour fournir des mesures précises sans contact avec le fluide.',
          ar: 'تستخدم مجموعة مقاييس التدفق بالموجات فوق الصوتية لدينا تقنيات متقدمة لقياس وقت العبور لتوفير قياسات دقيقة بدون تلامس مع السائل.',
          en: 'Our ultrasonic flowmeter range uses advanced transit time measurement techniques to provide accurate measurements without contact with the fluid.'
        },
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414 1.414m2.828 2.828a9 9 0 002.828 2.828m-8.485 0a9 9 0 01-2.828-2.828M6.343 6.343a9 9 0 00-2.828 2.828m8.485 8.485a5 5 0 01-7.072 0M3.515 12a9 9 0 010-6.364m12.728 2.121a9 9 0 012.828 2.828" />
          </svg>
        ),
        applications: [
          {fr: 'Mesure de débit liquide non-intrusive', ar: 'قياس تدفق السوائل دون تدخل', en: 'Non-intrusive liquid flow measurement'},
          {fr: 'Surveillance des conduites de gaz naturel', ar: 'مراقبة أنابيب الغاز الطبيعي', en: 'Natural gas pipeline monitoring'},
          {fr: 'Mesure de transfert de garde industriel', ar: 'قياس نقل الحراسة الصناعية', en: 'Industrial custody transfer measurement'}
        ]
      },
      {
        name: {
          fr: 'Technologie à effet vortex',
          ar: 'تقنية تأثير الدوامة',
          en: 'Vortex Technology'
        },
        description: {
          fr: 'Les débitmètres à effet vortex utilisent le principe de Kármán pour détecter et mesurer la fréquence des vortex qui se forment lors de l\'écoulement d\'un fluide autour d\'un obstacle.',
          ar: 'تستخدم عدادات التدفق ذات تأثير الدوامة مبدأ كارمان للكشف عن وقياس تردد الدوامات التي تتشكل عندما يتدفق السائل حول عائق.',
          en: 'Vortex flowmeters use the Kármán principle to detect and measure the frequency of vortices that form when fluid flows around an obstacle.'
        },
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
          </svg>
        ),
        applications: [
          {fr: 'Mesure de la vapeur dans les processus industriels', ar: 'قياس البخار في العمليات الصناعية', en: 'Steam measurement in industrial processes'},
          {fr: 'Contrôle des gaz industriels', ar: 'التحكم في الغازات الصناعية', en: 'Industrial gas control'},
          {fr: 'Mesure des liquides à viscosité variable', ar: 'قياس السوائل ذات اللزوجة المتغيرة', en: 'Variable viscosity liquid measurement'}
        ]
      },
      {
        name: {
          fr: 'Analyse paramagnétique d\'oxygène',
          ar: 'تحليل الأكسجين المغناطيسي',
          en: 'Paramagnetic Oxygen Analysis'
        },
        description: {
          fr: 'Notre technologie d\'analyse paramagnétique exploite les propriétés magnétiques uniques de l\'oxygène pour fournir des mesures précises de la concentration en oxygène dans divers environnements gazeux.',
          ar: 'تستغل تقنية التحليل المغناطيسي لدينا الخصائص المغناطيسية الفريدة للأكسجين لتوفير قياسات دقيقة لتركيز الأكسجين في بيئات غازية مختلفة.',
          en: 'Our paramagnetic analysis technology exploits the unique magnetic properties of oxygen to provide accurate measurements of oxygen concentration in various gaseous environments.'
        },
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2h2a2 2 0 002-2v-1a2 2 0 012-2h1.945M5.055 15H15M5.055 19h9M15 11h4a2 2 0 01.82 3.84" />
          </svg>
        ),
        applications: [
          {fr: 'Contrôle de pureté des gaz industriels', ar: 'مراقبة نقاء الغازات الصناعية', en: 'Industrial gas purity control'},
          {fr: 'Surveillance des processus de combustion', ar: 'مراقبة عمليات الاحتراق', en: 'Combustion process monitoring'},
          {fr: 'Analyses environnementales', ar: 'التحليلات البيئية', en: 'Environmental analysis'}
        ]
      }
    ],
    specifications: [
      {
        category: {
          fr: 'Précision',
          ar: 'الدقة',
          en: 'Precision'
        },
        items: [
          {
            name: {
              fr: 'Débitmètres à ultrasons',
              ar: 'مقاييس التدفق بالموجات فوق الصوتية',
              en: 'Ultrasonic flowmeters'
            },
            value: {
              fr: '±0.5% à ±1.0% de la lecture',
              ar: '±0.5% إلى ±1.0% من القراءة',
              en: '±0.5% to ±1.0% of reading'
            }
          },
          {
            name: {
              fr: 'Débitmètres à effet vortex',
              ar: 'مقاييس التدفق بتأثير الدوامة',
              en: 'Vortex flowmeters'
            },
            value: {
              fr: '±0.7% à ±1.5% de la lecture',
              ar: '±0.7% إلى ±1.5% من القراءة',
              en: '±0.7% to ±1.5% of reading'
            }
          },
          {
            name: {
              fr: 'Analyseurs d\'oxygène',
              ar: 'محللات الأكسجين',
              en: 'Oxygen analyzers'
            },
            value: {
              fr: '±0.1% O₂',
              ar: '±0.1% O₂',
              en: '±0.1% O₂'
            }
          },
          {
            name: {
              fr: 'Analyseurs d\'humidité',
              ar: 'محللات الرطوبة',
              en: 'Humidity analyzers'
            },
            value: {
              fr: '±2°C point de rosée',
              ar: '±2°C نقطة الندى',
              en: '±2°C dew point'
            }
          }
        ]
      },
      {
        category: {
          fr: 'Plages de mesure',
          ar: 'نطاقات القياس',
          en: 'Measurement Ranges'
        },
        items: [
          {
            name: {
              fr: 'Débitmètres liquides',
              ar: 'مقاييس تدفق السوائل',
              en: 'Liquid flowmeters'
            },
            value: {
              fr: '0.03 à 12 m/s',
              ar: '0.03 إلى 12 متر/ثانية',
              en: '0.03 to 12 m/s'
            }
          },
          {
            name: {
              fr: 'Débitmètres gaz',
              ar: 'مقاييس تدفق الغاز',
              en: 'Gas flowmeters'
            },
            value: {
              fr: '0.1 à 120 m/s',
              ar: '0.1 إلى 120 متر/ثانية',
              en: '0.1 to 120 m/s'
            }
          },
          {
            name: {
              fr: 'Analyseurs d\'oxygène',
              ar: 'محللات الأكسجين',
              en: 'Oxygen analyzers'
            },
            value: {
              fr: '0-100% O₂',
              ar: '0-100% O₂',
              en: '0-100% O₂'
            }
          },
          {
            name: {
              fr: 'Analyseurs d\'humidité',
              ar: 'محللات الرطوبة',
              en: 'Humidity analyzers'
            },
            value: {
              fr: '-110°C à +20°C point de rosée',
              ar: '-110°C إلى +20°C نقطة الندى',
              en: '-110°C to +20°C dew point'
            }
          }
        ]
      },
      {
        category: {
          fr: 'Environnement',
          ar: 'البيئة',
          en: 'Environment'
        },
        items: [
          {
            name: {
              fr: 'Température ambiante',
              ar: 'درجة الحرارة المحيطة',
              en: 'Ambient temperature'
            },
            value: {
              fr: '-40°C à +60°C',
              ar: '-40°C إلى +60°C',
              en: '-40°C to +60°C'
            }
          },
          {
            name: {
              fr: 'Classification IP',
              ar: 'تصنيف IP',
              en: 'IP Rating'
            },
            value: {
              fr: 'IP66/67',
              ar: 'IP66/67',
              en: 'IP66/67'
            }
          },
          {
            name: {
              fr: 'Certification zones dangereuses',
              ar: 'شهادة المناطق الخطرة',
              en: 'Hazardous area certification'
            },
            value: {
              fr: 'ATEX, IECEx, CSA',
              ar: 'ATEX, IECEx, CSA',
              en: 'ATEX, IECEx, CSA'
            }
          },
          {
            name: {
              fr: 'Résistance aux vibrations',
              ar: 'مقاومة الاهتزازات',
              en: 'Vibration resistance'
            },
            value: {
              fr: 'IEC 60068-2-6',
              ar: 'IEC 60068-2-6',
              en: 'IEC 60068-2-6'
            }
          }
        ]
      }
    ],
    certifications: [
      {
        name: {
          fr: 'ATEX',
          ar: 'ATEX',
          en: 'ATEX'
        },
        description: {
          fr: 'Certification pour l\'utilisation dans les atmosphères explosibles selon les directives européennes.',
          ar: 'شهادة للاستخدام في الأجواء القابلة للانفجار وفقاً للتوجيهات الأوروبية.',
          en: 'Certification for use in explosive atmospheres according to European directives.'
        },
        icon: '🇪🇺'
      },
      {
        name: {
          fr: 'IECEx',
          ar: 'IECEx',
          en: 'IECEx'
        },
        description: {
          fr: 'Système de certification internationale pour les équipements utilisés dans les atmosphères explosibles.',
          ar: 'نظام الشهادات الدولية للمعدات المستخدمة في الأجواء القابلة للانفجار.',
          en: 'International certification system for equipment used in explosive atmospheres.'
        },
        icon: '🌍'
      },
      {
        name: {
          fr: 'CSA',
          ar: 'CSA',
          en: 'CSA'
        },
        description: {
          fr: 'Certification canadienne pour la sécurité des équipements électriques.',
          ar: 'الشهادة الكندية لسلامة المعدات الكهربائية.',
          en: 'Canadian certification for electrical equipment safety.'
        },
        icon: '🇨🇦'
      },
      {
        name: {
          fr: 'SIL2',
          ar: 'SIL2',
          en: 'SIL2'
        },
        description: {
          fr: 'Niveau d\'intégrité de sécurité pour les applications critiques.',
          ar: 'مستوى سلامة التكامل للتطبيقات الحرجة.',
          en: 'Safety integrity level for critical applications.'
        },
        icon: '🔒'
      }
    ]
  };

  return (
    <section className="section section-dark" style={{ 
      padding: '5rem 0',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative patterns */}
      <div style={{
        position: 'absolute',
        top: '5%',
        left: '5%',
        width: '200px',
        height: '200px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255, 107, 0, 0.03) 0%, rgba(255, 107, 0, 0) 70%)',
        zIndex: 1
      }}></div>
      
      <div style={{
        position: 'absolute',
        bottom: '10%',
        right: '5%',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255, 107, 0, 0.03) 0%, rgba(255, 107, 0, 0) 70%)',
        zIndex: 1
      }}></div>
      
      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem'
        }}>
          <span style={{
            display: 'inline-block',
            padding: '0.5rem 1.5rem',
            backgroundColor: 'rgba(255, 107, 0, 0.1)',
            color: 'var(--assco-orange)',
            borderRadius: '30px',
            fontWeight: '600',
            fontSize: '0.875rem',
            marginBottom: '1rem',
            textTransform: 'uppercase'
          }}>
            {language === 'fr' ? 'Documentation Technique' : 
             language === 'ar' ? 'التوثيق التقني' : 
             'Technical Documentation'}
          </span>

          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            marginBottom: '1.5rem',
            color: 'white',
            position: 'relative',
            display: 'inline-block'
          }}>
            {language === 'fr' ? 'Spécifications & Certifications' : 
             language === 'ar' ? 'المواصفات والشهادات' : 
             'Specifications & Certifications'}
            <span style={{
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '80px',
              height: '4px',
              backgroundColor: 'var(--assco-orange)',
              borderRadius: '2px'
            }}></span>
          </h2>
          <p style={{
            fontSize: '1.125rem',
            color: 'rgba(255, 255, 255, 0.85)',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.7'
          }}>
            {language === 'fr' 
              ? 'Découvrez les détails techniques et les certifications de notre gamme complète d\'instruments de mesure industriels.' 
              : language === 'ar'
              ? 'اكتشف التفاصيل التقنية والشهادات لمجموعتنا الكاملة من أدوات القياس الصناعية.'
              : 'Discover the technical details and certifications of our complete range of industrial measurement instruments.'}
          </p>
        </div>
        
        {/* Navigation Tabs */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
          marginBottom: '3rem'
        }}>
          <button 
            onClick={() => setActiveTab('specifications')}
            style={{
              padding: '1rem 2rem',
              fontSize: '1.125rem',
              fontWeight: '600',
              color: activeTab === 'specifications' ? 'var(--assco-orange)' : 'rgba(255, 255, 255, 0.7)',
              borderBottom: activeTab === 'specifications' ? '3px solid var(--assco-orange)' : '3px solid transparent',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            {language === 'fr' ? 'Spécifications' : 
             language === 'ar' ? 'المواصفات' : 
             'Specifications'}
          </button>
          <button 
            onClick={() => setActiveTab('technologies')}
            style={{
              padding: '1rem 2rem',
              fontSize: '1.125rem',
              fontWeight: '600',
              color: activeTab === 'technologies' ? 'var(--assco-orange)' : 'rgba(255, 255, 255, 0.7)',
              borderBottom: activeTab === 'technologies' ? '3px solid var(--assco-orange)' : '3px solid transparent',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            {language === 'fr' ? 'Technologies' : 
             language === 'ar' ? 'التقنيات' : 
             'Technologies'}
          </button>
          <button 
            onClick={() => setActiveTab('certifications')}
            style={{
              padding: '1rem 2rem',
              fontSize: '1.125rem',
              fontWeight: '600',
              color: activeTab === 'certifications' ? 'var(--assco-orange)' : 'rgba(255, 255, 255, 0.7)',
              borderBottom: activeTab === 'certifications' ? '3px solid var(--assco-orange)' : '3px solid transparent',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            {language === 'fr' ? 'Certifications' : 
             language === 'ar' ? 'الشهادات' : 
             'Certifications'}
          </button>
        </div>
        
        {/* Content Area */}
        <div className="tab-content">
          {/* Specifications Tab */}
          {activeTab === 'specifications' && (
            <div className="specifications-content" style={{
              animation: 'fadeIn 0.5s ease'
            }}>
              {productSpecs.specifications.map((specCategory, index) => (
                <div key={index} style={{
                  marginBottom: index < productSpecs.specifications.length - 1 ? '3rem' : '0'
                }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    marginBottom: '1.5rem',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--assco-orange)',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: '700'
                    }}>
                      {index + 1}
                    </span>
                    {language === 'fr' ? specCategory.category.fr : 
                     language === 'ar' ? specCategory.category.ar : 
                     specCategory.category.en}
                  </h3>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '1.5rem'
                  }}>
                    {specCategory.items.map((item, itemIndex) => (
                      <div key={itemIndex} style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        padding: '1.5rem',
                        borderRadius: '0.75rem',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.25)';
                        e.currentTarget.style.borderColor = 'rgba(255, 107, 0, 0.5)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      }}>
                        <h4 style={{
                          fontSize: '1.125rem',
                          fontWeight: '600',
                          marginBottom: '0.75rem',
                          color: 'white'
                        }}>
                          {language === 'fr' ? item.name.fr : 
                           language === 'ar' ? item.name.ar : 
                           item.name.en}
                        </h4>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <span style={{
                            color: 'var(--assco-orange)',
                            fontWeight: '600'
                          }}>
                            <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '20px', height: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </span>
                          <span style={{
                            fontSize: '1.125rem',
                            color: 'rgba(255, 255, 255, 0.85)',
                            fontWeight: '500'
                          }}>
                            {language === 'fr' ? item.value.fr : 
                             language === 'ar' ? item.value.ar : 
                             item.value.en}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Technologies Tab */}
          {activeTab === 'technologies' && (
            <div className="technologies-content" style={{
              animation: 'fadeIn 0.5s ease'
            }}>
              {productSpecs.technologies.map((tech, index) => (
                <div 
                  key={index} 
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '1rem',
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                    marginBottom: index < productSpecs.technologies.length - 1 ? '2rem' : '0',
                    display: 'grid',
                    gridTemplateColumns: '280px 1fr',
                    transition: 'all 0.3s ease'
                  }}
                  className="fade-in"
                  onMouseOver={(e) => {
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.25)';
                    e.currentTarget.style.transform = 'translateY(-5px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{
                    padding: '2rem',
                    backgroundColor: `hsl(${(index * 40) % 360}, 75%, 97%)`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {/* Decorative background */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      opacity: 0.1,
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23${index === 0 ? 'FF6B00' : index === 1 ? '3182ce' : '805ad5'}' fill-opacity='0.2'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                    }}></div>
                    
                    <div style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '50%',
                      backgroundColor: `hsl(${(index * 40) % 360}, 80%, 92%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '1.5rem',
                      position: 'relative',
                      zIndex: 2,
                      color: `hsl(${(index * 40) % 360}, 80%, 45%)`,
                      boxShadow: `0 8px 16px rgba(${index === 0 ? '255, 107, 0' : index === 1 ? '49, 130, 206' : '128, 90, 213'}, 0.2)`
                    }}>
                      <div style={{ width: '60px', height: '60px' }}>
                        {tech.icon}
                      </div>
                    </div>
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: '700',
                      textAlign: 'center',
                      color: 'white',
                      marginBottom: '1rem',
                      position: 'relative',
                      zIndex: 2
                    }}>
                      {language === 'fr' ? tech.name.fr : 
                       language === 'ar' ? tech.name.ar : 
                       tech.name.en}
                    </h3>
                  </div>
                  
                  <div style={{ padding: '2rem' }}>
                    <p style={{
                      fontSize: '1rem',
                      lineHeight: '1.7',
                      color: 'rgba(255, 255, 255, 0.85)',
                      marginBottom: '1.5rem'
                    }}>
                      {language === 'fr' ? tech.description.fr : 
                       language === 'ar' ? tech.description.ar : 
                       tech.description.en}
                    </p>
                    
                    <h4 style={{
                      fontSize: '1rem',
                      fontWeight: '700',
                      marginBottom: '1rem',
                      color: 'white'
                    }}>
                      {language === 'fr' ? 'Applications principales:' : 
                       language === 'ar' ? 'التطبيقات الرئيسية:' : 
                       'Main applications:'}
                    </h4>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                      gap: '1rem'
                    }}>
                      {tech.applications.map((app, appIndex) => (
                        <div key={appIndex} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 0.75rem',
                          backgroundColor: `rgba(0, 0, 0, 0.2)`,
                          borderRadius: '0.5rem',
                          color: 'rgba(255, 255, 255, 0.85)',
                          fontSize: '0.95rem'
                        }}>
                          <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '18px', height: '18px', color: `hsl(${(index * 40) % 360}, 80%, 45%)` }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {language === 'fr' ? app.fr : 
                           language === 'ar' ? app.ar : 
                           app.en}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Certifications Tab */}
          {activeTab === 'certifications' && (
            <div className="certifications-content" style={{
              animation: 'fadeIn 0.5s ease',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '2rem'
            }}>
              {productSpecs.certifications.map((cert, index) => (
                <div key={index} style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  padding: '2rem',
                  borderRadius: '1rem',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.25)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15)';
                }}>
                  <div style={{
                    fontSize: '3rem',
                    marginBottom: '1rem'
                  }}>
                    {cert.icon}
                  </div>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    marginBottom: '1rem',
                    color: '#1a202c'
                  }}>
                    {language === 'fr' ? cert.name.fr : 
                     language === 'ar' ? cert.name.ar : 
                     cert.name.en}
                  </h3>
                  <p style={{
                    fontSize: '1rem',
                    color: 'rgba(255, 255, 255, 0.85)',
                    lineHeight: '1.6',
                    flex: '1'
                  }}>
                    {language === 'fr' ? cert.description.fr : 
                     language === 'ar' ? cert.description.ar : 
                     cert.description.en}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .fade-in {
          animation: fadeIn 0.5s ease forwards;
        }
        
        @media (max-width: 768px) {
          .technologies-content > div {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
};

export default ProductSpecificationsSection;
